/**
 * useSEO
 * Dynamically sets <title>, <meta description>, Open Graph and Twitter Card tags
 * for each page. Call at the top of every page component.
 *
 * Usage:
 *   useSEO({
 *     title: "Eletricidade no Porto | Serviway",
 *     description: "Instalações e reparações elétricas por técnicos certificados...",
 *     image: "/manus-storage/serviway-hero-main_cee7afbe.jpg", // optional
 *     url: "https://www.serviway.pt/servicos/eletricidade", // optional
 *   });
 */

import { useContext, useEffect } from "react";
import { MetadataContext, type SEOProps } from "@/ssr/metadata";
import { SITE_ORIGIN } from "@shared/siteRoutes";

const DEFAULT_IMAGE =
  "/manus-storage/serviway-hero-main_cee7afbe.jpg";
const SITE_NAME = "Serviway";

function setMeta(name: string, content: string, attr: "name" | "property" = "name") {
  let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setLink(rel: string, href: string) {
  let el = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

export function useSEO({ title, description, image, url, type = "website", noindex = false, publishedAt, modifiedAt }: SEOProps) {
  const collector = useContext(MetadataContext);
  if (collector) collector.seo = { title, description, image, url, type, noindex, publishedAt, modifiedAt };
  useEffect(() => {
    const fullTitle = title.includes("Serviway") ? title : `${title} | ${SITE_NAME}`;
    const ogImage = new URL(image || DEFAULT_IMAGE, SITE_ORIGIN).href;
    const canonical = url || window.location.href.split("?")[0].split("#")[0];

    // Title
    document.title = fullTitle;
    document.querySelectorAll('script[data-ssr-jsonld]').forEach(el => el.remove());

    // Basic meta
    setMeta("description", description);
    setMeta("robots", noindex ? "noindex, follow" : "index, follow");

    // Canonical
    if (noindex) document.querySelector('link[rel="canonical"]')?.remove();
    else setLink("canonical", canonical);
    for (const [property, value] of [["article:published_time", publishedAt], ["article:modified_time", modifiedAt]]) {
      if (type === "article" && value) setMeta(property!, value, "property");
      else document.querySelector(`meta[property="${property}"]`)?.remove();
    }

    // Open Graph
    setMeta("og:type", type, "property");
    setMeta("og:title", fullTitle, "property");
    setMeta("og:description", description, "property");
    setMeta("og:image", ogImage, "property");
    setMeta("og:url", canonical, "property");
    setMeta("og:site_name", SITE_NAME, "property");
    setMeta("og:locale", "pt_PT", "property");

    // Twitter Card
    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", fullTitle);
    setMeta("twitter:description", description);
    setMeta("twitter:image", ogImage);
  }, [title, description, image, url, type, noindex, publishedAt, modifiedAt]);
}
